import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { formatEUR, formatDate, today } from "../lib/format";
import { Plus, Trash, PencilSimple, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Field, Modal } from "./AVGS";

const empty = {
  customer_name: "",
  amount: 0,
  payment_date: today(),
  invoice_type: "OTHER",
  invoice_id: null,
  notes: "",
};

export default function PaymentsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/payments");
    setItems(data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(empty); setCandidates([]); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it.id);
    setForm({
      customer_name: it.customer_name || "",
      amount: it.amount || 0,
      payment_date: it.payment_date || today(),
      invoice_type: it.invoice_type || "OTHER",
      invoice_id: it.invoice_id || null,
      notes: it.notes || "",
    });
    setCandidates([]);
    setOpen(true);
  };

  const fetchSuggestions = async () => {
    if (!form.customer_name && !form.amount) return;
    try {
      const { data } = await api.post("/payments/match", {
        ...form,
        amount: parseFloat(form.amount || 0),
      });
      setCandidates(data.candidates || []);
    } catch (e) {
      console.error("payment match request failed", e);
    }
  };

  const pickCandidate = (c) => {
    setForm({ ...form, invoice_id: c.invoice_id, invoice_type: c.invoice_type });
    toast.success(`Zuordnung: ${c.customer_name} (${c.invoice_type})`);
  };

  const clearLink = () => setForm({ ...form, invoice_id: null, invoice_type: "OTHER" });

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: parseFloat(form.amount || 0) };
      if (editing) {
        await api.put(`/payments/${editing}`, payload);
        toast.success("Zahlung aktualisiert");
      } else {
        await api.post("/payments", payload);
        toast.success("Zahlung erfasst");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Zahlung wirklich löschen?")) return;
    await api.delete(`/payments/${id}`);
    toast.success("Gelöscht");
    load();
  };

  return (
    <div className="space-y-6" data-testid="payments-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">Zahlungseingänge</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1.5">Eingegangene Zahlungen</h1>
          <p className="text-sm text-[#5B6371] mt-1">Erfasste Zahlungen mit automatischer Zuordnung zu offenen Rechnungen.</p>
        </div>
        <button onClick={openCreate}
          data-testid="payment-create-button"
          className="inline-flex items-center gap-2 bg-[#0AA09E] hover:bg-[#088987] text-white text-sm font-medium rounded-md px-4 py-2.5">
          <Plus size={16} weight="bold" /> Zahlung erfassen
        </button>
      </header>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-table">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-[#5B6371] border-b-2 border-[#E5E7EB] bg-[#F8FAFC]">
                <th className="text-left px-4 py-3 font-medium">Datum</th>
                <th className="text-left px-4 py-3 font-medium">Kunde</th>
                <th className="text-right px-4 py-3 font-medium">Betrag</th>
                <th className="text-left px-4 py-3 font-medium">Zuordnung</th>
                <th className="text-left px-4 py-3 font-medium">Notiz</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[#94A3B8]">Lade …</td></tr>)}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-[#94A3B8]">Noch keine Zahlungen erfasst.</td></tr>
              )}
              {items.map((it) => (
                <tr key={it.id} className="border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]" data-testid={`payment-row-${it.id}`}>
                  <td className="px-4 py-3">{formatDate(it.payment_date)}</td>
                  <td className="px-4 py-3 font-medium">{it.customer_name}</td>
                  <td className="px-4 py-3 text-right font-medium text-[#0AA09E]">{formatEUR(it.amount)}</td>
                  <td className="px-4 py-3">
                    {it.invoice_id ? (
                      <span className="text-xs px-2 py-0.5 rounded bg-[#E5E7EB] text-[#1F242E]">{it.invoice_type}</span>
                    ) : (
                      <span className="text-xs text-[#94A3B8]">Nicht zugeordnet</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#5B6371] text-xs truncate max-w-[200px]">{it.notes || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(it)} className="text-[#5B6371] hover:text-[#0AA09E] p-1.5" data-testid={`payment-edit-${it.id}`}>
                      <PencilSimple size={16} />
                    </button>
                    <button onClick={() => onDelete(it.id)} className="text-[#5B6371] hover:text-[#FF6B5C] p-1.5" data-testid={`payment-delete-${it.id}`}>
                      <Trash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <Modal title={editing ? "Zahlung bearbeiten" : "Zahlungseingang erfassen"} onClose={() => setOpen(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="payment-form">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Kunde / Teilnehmer">
                <input required className="field" value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  onBlur={fetchSuggestions}
                  data-testid="payment-customer" />
              </Field>
              <Field label="Datum">
                <input type="date" required className="field" value={form.payment_date}
                  onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                  data-testid="payment-date" />
              </Field>
            </div>
            <Field label="Betrag (€)">
              <input type="number" step="0.01" required className="field" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                onBlur={fetchSuggestions}
                data-testid="payment-amount" />
            </Field>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 label-eyebrow"><Sparkle size={14} weight="duotone" /> Auto-Vorschlag</div>
                <button type="button" onClick={fetchSuggestions}
                  className="text-xs text-[#0AA09E] hover:underline" data-testid="payment-suggest">
                  Vorschlagen
                </button>
              </div>
              {form.invoice_id && (
                <div className="flex items-center justify-between bg-[#0AA09E] text-white rounded-md px-3 py-2 mb-2">
                  <span className="text-xs">Zugeordnet: {form.invoice_type}</span>
                  <button type="button" onClick={clearLink} className="text-xs underline" data-testid="payment-unlink">Entfernen</button>
                </div>
              )}
              {candidates.length === 0 ? (
                <div className="text-xs text-[#94A3B8] bg-[#F8FAFC] rounded-md px-3 py-2 border border-[#E5E7EB]">
                  Kunde/Betrag eingeben, dann "Vorschlagen" klicken.
                </div>
              ) : (
                <div className="space-y-1.5 max-h-44 overflow-y-auto" data-testid="payment-candidates">
                  {candidates.map((c, idx) => (
                    <button
                      type="button"
                      key={`${c.invoice_type}-${c.invoice_id}`}
                      onClick={() => pickCandidate(c)}
                      className={`w-full flex items-center justify-between px-3 py-2 border rounded-md text-sm text-left transition-colors ${
                        form.invoice_id === c.invoice_id
                          ? "bg-[#E6F7F7] border-[#0AA09E]"
                          : "bg-white border-[#E5E7EB] hover:border-[#E2E8F0]"
                      }`}
                      data-testid={`payment-candidate-${idx}`}
                    >
                      <div>
                        <div className="font-medium">{c.customer_name}</div>
                        <div className="text-xs text-[#5B6371]">{c.invoice_type}{c.month ? ` · ${c.month}` : ""} · {formatEUR(c.total_amount)}</div>
                      </div>
                      <div className="text-xs text-[#0AA09E] font-medium">{c.score}%</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Field label="Notiz">
              <textarea className="field" rows={2} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="payment-notes" />
            </Field>

            <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E7EB]">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-[#5B6371]" data-testid="payment-cancel">Abbrechen</button>
              <button type="submit" className="px-5 py-2 bg-[#0AA09E] hover:bg-[#088987] text-white text-sm font-medium rounded-md" data-testid="payment-submit">Speichern</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
